import { Box, CircularProgress, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import TaskCategoryService from '../../services/TaskCategoryService';
import TaskPlatformService from '../../services/TaskPlatformService';
import AdminPageWrapper from '../admin/AdminPageWrapper';
import DeletedPostsList from './DeletedPostsList';
import EmailsTab from './EmailsTab';
import EnglishFormsSection from './EnglishFormsSection';
import EnglishSrtsList from './EnglishSrtsList';
import { refreshTaskCategories, refreshTaskPlatforms, useTaskCategories, useTaskPlatforms } from './taskFields';
import TaskImportPanel from './TaskImportPanel';
import TaskListEditor from './TaskListEditor';
import useTaskSummary from './useTaskSummary';

// The tab sits in the address, so the old pages' links can land on theirs
const TABS = [
    { key: 'english', label: 'English' },
    { key: 'import', label: 'Import from SmartSheet' },
    { key: 'lists', label: 'Categories and platforms', adminOnly: true },
    { key: 'deleted', label: 'Deleted posts', adminOnly: true },
    { key: 'emails', label: 'Emails', adminOnly: true },
];

const Section = ({ title, description, children }) => (
    <Paper sx={{ p: 3 }}>
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {description}
        </Typography>
        {children}
    </Paper>
);

const Lists = () => {
    const categories = useTaskCategories();
    const platforms = useTaskPlatforms();
    return (
        <>
            <Section
                title="Categories and buckets"
                description="Category on videos, text files and books. Bucket on posts. It's one list, so a change here shows in both. Hiding one keeps it on what already has it."
            >
                <TaskListEditor
                    noun="category"
                    items={categories}
                    service={TaskCategoryService}
                    refresh={refreshTaskCategories}
                />
            </Section>
            <Section
                title="Platforms"
                description="Where a video or post goes out. Hiding one keeps it on what already has it. Instagram/Facebook never offers English, even if it's renamed."
            >
                <TaskListEditor
                    noun="platform"
                    items={platforms}
                    service={TaskPlatformService}
                    refresh={refreshTaskPlatforms}
                />
            </Section>
        </>
    );
};

/**
 * /tasks/tools: what PMs and admins use now and then, as tabs on one page. PMs get English and
 * Import; admins also keep the lists, deleted posts and emails. The server checks each call itself.
 */
const TaskToolsPage = ({ darkMode, toggleDarkMode, themeVariant }) => {
    useTrackUsage('TaskToolsPage', 'screen', 'tasks');
    const summary = useTaskSummary();
    const [params, setParams] = useSearchParams();

    if (summary && !summary.canManage) {
        return <Navigate to="/tasks" replace />;
    }
    const tabs = TABS.filter((t) => !t.adminOnly || summary?.isAdmin);
    const tab = tabs.find((t) => t.key === params.get('tab'))?.key || tabs[0].key;

    return (
        <AdminPageWrapper
            title="Task tools"
            showNavbar={!!summary?.isAdmin}
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            themeVariant={themeVariant}
        >
            <Box sx={{ maxWidth: 1100, mx: 'auto', pb: 4 }}>
                <Typography variant="h4">Task tools</Typography>
                {!summary ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Tabs
                            value={tab}
                            onChange={(e, key) => setParams({ tab: key }, { replace: true })}
                            variant="scrollable"
                            allowScrollButtonsMobile
                            sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
                        >
                            {tabs.map((t) => (
                                <Tab key={t.key} value={t.key} label={t.label} />
                            ))}
                        </Tabs>
                        <Stack spacing={3}>
                            {tab === 'english' && (
                                <>
                                    <Section
                                        title="English SRTs"
                                        description="What AV sends through the SRT form. Older SRTs are still on Drive."
                                    >
                                        <EnglishSrtsList />
                                    </Section>
                                    {summary.isAdmin && (
                                        <Section
                                            title="Form links"
                                            description="No-login links for AV. Each kind has one live link. Turning one off closes it, and a new link is a new address."
                                        >
                                            <EnglishFormsSection />
                                        </Section>
                                    )}
                                </>
                            )}
                            {tab === 'import' && (
                                <Section
                                    title="Import from SmartSheet"
                                    description="Export your SmartSheet as CSV and choose it here. Each row becomes a task at its current stage, with earlier stages marked done. Nothing is saved until you import, and you can check the file as often as you like."
                                >
                                    <Box sx={{ mt: 2 }}>
                                        <TaskImportPanel />
                                    </Box>
                                </Section>
                            )}
                            {tab === 'lists' && <Lists />}
                            {tab === 'emails' && <EmailsTab />}
                            {tab === 'deleted' && (
                                <Section
                                    title="Deleted posts"
                                    description="Posts stay here for 30 days. After that their text and files are removed."
                                >
                                    <DeletedPostsList />
                                </Section>
                            )}
                        </Stack>
                    </>
                )}
            </Box>
        </AdminPageWrapper>
    );
};

export default TaskToolsPage;
